import { DetailsRow } from "~/components/details/DetailsSection"
import { RoleBadge } from "~/components/RoleBadge"
import { type ReactNode, useState } from "react"
import { EntityIdentity, getUserPresence } from "~/components/EntityIdentity"
import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger
} from "~/components/ui/hover-card"
import { cn } from "~/lib/utils"
import { useOrganization } from "~/hooks/useOrganization"

export type UserHoverCardMember = {
	id: string
	name: string
	image: string | null
	lastSeenAt: Date | null
	timezone: string | null
	teams: {
		id: string
		name: string
	}[]
	teamRoles: {
		id: string
		name: string
		color: string
	}[]
}

function formatUserLocalTime(timezone: string | null) {
	if (!timezone) return "Time zone not set"

	try {
		return new Intl.DateTimeFormat(undefined, {
			hour: "numeric",
			minute: "2-digit",
			timeZone: timezone,
			timeZoneName: "short"
		}).format(new Date())
	} catch {
		return "Time zone unavailable"
	}
}

export function UserHoverCard({
	member: providedMember,
	userId,
	children
}: {
	member?: UserHoverCardMember
	userId?: string
	children: ReactNode
}) {
	const { organization } = useOrganization()
	const [triggerElement, setTriggerElement] = useState<HTMLSpanElement | null>(
		null
	)
	const orgMember = userId
		? organization?.members.find((member) => member.userId === userId)
		: undefined
	const teams = orgMember
		? (organization?.teams.filter((team) =>
				team.teamMembers.some((member) => member.orgMemberId === orgMember.id)
			) ?? [])
		: []
	const member =
		providedMember ??
		(orgMember
			? {
					...orgMember.user,
					teams: teams.map(({ id, name }) => ({ id, name })),
					teamRoles: [
						...new Map(
							teams
								.flatMap((team) =>
									team.teamMembers
										.filter((member) => member.orgMemberId === orgMember.id)
										.flatMap((member) => member.roles)
								)
								.map((role) => [role.id, role])
						).values()
					]
				}
			: undefined)
	if (!member) return children
	const presenceStatus = getUserPresence(member.lastSeenAt)

	return (
		<HoverCard openDelay={500} closeDelay={100}>
			<HoverCardTrigger asChild>
				<span
					ref={setTriggerElement}
					className="inline-flex min-w-0 max-w-full cursor-default align-baseline"
				>
					{children}
				</span>
			</HoverCardTrigger>
			<HoverCardContent
				container={
					triggerElement?.closest<HTMLElement>(
						'[role="dialog"], [role="alertdialog"]'
					) ?? undefined
				}
				align="start"
				className="w-80 bg-background-secondary"
			>
				<EntityIdentity
					kind="user"
					name={member.name}
					image={member.image}
					lastSeenAt={member.lastSeenAt}
					size="lg"
				/>
				<hr className="my-3 border-stroke" />
				<div className="flex flex-col gap-2 text-sm">
					<DetailsRow orientation="spread" label={<> Status </>}>
						<span
							className={cn(
								"text-text-main",
								presenceStatus === "active" && "text-green-400",
								presenceStatus === "recent" && "text-yellow-300"
							)}
						>
							{presenceStatus === "active"
								? "Online"
								: presenceStatus === "recent"
									? "Idle"
									: "Offline"}
						</span>
					</DetailsRow>
					<DetailsRow orientation="spread" label={<> Local time </>}>
						<span className="text-text-main">
							{formatUserLocalTime(member.timezone)}
						</span>
					</DetailsRow>
					<div className="flex flex-col gap-1.5">
						<span className="text-text-small">Teams</span>
						<div className="flex flex-wrap gap-1">
							{member.teams.length > 0 ? (
								member.teams.map((team) => (
									<span
										key={team.id}
										className="rounded-full border border-stroke px-2 py-1 text-xs text-text-main"
									>
										{team.name}
									</span>
								))
							) : (
								<span className="text-xs text-text-small">No teams</span>
							)}
						</div>
					</div>
					<div className="flex flex-col gap-1.5">
						<span className="text-text-small">Team roles</span>
						<div className="flex flex-wrap gap-1">
							{member.teamRoles.length > 0 ? (
								member.teamRoles.map((role) => (
									<RoleBadge role={role} key={role.id} />
								))
							) : (
								<span className="text-xs text-text-small">No roles</span>
							)}
						</div>
					</div>
				</div>
			</HoverCardContent>
		</HoverCard>
	)
}
